import { IObservable, Observable } from '@1js/oil-externals/rx';
import { take } from 'rxjs/operators';

import { FileItem, FileType } from '../../shared/models/file-item.model';
import { MruDataService } from '../../shared/mru/mru-data.service';
import { DeviceDetector } from '../../shared/services/device-detector.service';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { PathArrayFormatter } from '../../shared/utilities/path-array-formatter.utility';
import { UrlBuilder } from '../../shared/utilities/url-builder.utility';
import { SearchInstrumentationService, SearchResultType } from '../search-instrumentation.service';
import { SharedSearchService } from '../shared-search.service';

export class FilesFastSuggestionProvider {
    public static capabilitiesService: CapabilitiesService;
    public static clientStartupConfigService: ClientStartupConfigService;
    public static contextService: ContextService;
    public static deviceDetector: DeviceDetector;
    public static mruDataService: MruDataService;
    public static secondaryDisplayTextEnabled: boolean;
    public static searchInstrumentationService: SearchInstrumentationService;
    public static sharedSearchService: SharedSearchService;
    private static readonly pathCharacterLimit = 60;

    public createStream(searchText: string): IObservable<any /* TODO: Change to ISearchSuggestion when it's exported*/> {
        const recentMruObservable = FilesFastSuggestionProvider.mruDataService.getMruDocuments();

        return Observable['fromPromise'](
            // getMruDocuments() returns observable that replays last value so take(1) will pick it up immediately if it is already available;
            // otherwise, take(1) will wait for next value until it comes
            recentMruObservable.results.pipe(take(1)).toPromise().then(fileData => {
                if (fileData) {
                    let files = fileData.items || [];

                    if (FilesFastSuggestionProvider.clientStartupConfigService.config.authType !== AuthType.MSA && FilesFastSuggestionProvider.capabilitiesService?.capabilities.isPwaInUwp && FilesFastSuggestionProvider.capabilitiesService?.capabilities.nativeSearchInHeaderEnabled) {
                        files = files.filter(file => file.IsLocal);
                    }

                    return files.map(file => {
                        const fileType = FileItem.fileTypeNameToFileType(file.FileType);
                        return {
                            id: decodeURI(file.Url),
                            isLocal: file.IsLocal,
                            url: file.IsLocal ? file.Url : UrlBuilder.appendQuery(file.Url, 'web=1'),
                            webUrl: file.WebUrl,
                            title: file.Title,
                            iconUrl: file.Url,
                            type: 'file',
                            fileType: file.FileType,
                            iconClass: `${FileItem.documentIconFromType(fileType, '32x32')} file-suggestion-icon-fix`,
                            fileExtension: fileType === FileType.OneNote ? 'one' : FileItem.getFileExtensionFromUrl(file.Url),
                            secondaryDisplayText: FilesFastSuggestionProvider.secondaryDisplayTextEnabled ?
                                PathArrayFormatter.Format(file.PathArray, FilesFastSuggestionProvider.contextService.isRtl, FilesFastSuggestionProvider.pathCharacterLimit)[0] :
                                '',
                            searchInstrumentationService: FilesFastSuggestionProvider.searchInstrumentationService,
                            sharedSearchService: FilesFastSuggestionProvider.sharedSearchService
                        };
                    });
                }
                return [];
            })
        ).select((suggestions: Array<any>) =>
                suggestions.filter(suggestion =>
                    !searchText || (suggestion.title && suggestion.title.toLocaleLowerCase().indexOf(searchText.toLocaleLowerCase().trim()) !== -1)))
            .selectMany(fileSuggestions => {
                if (fileSuggestions.length > 0) {
                    FilesFastSuggestionProvider.searchInstrumentationService.setRenderedProviderResults(SearchResultType.File, ['']);
                } else {
                    FilesFastSuggestionProvider.searchInstrumentationService.setRenderedProviderResults(SearchResultType.File, []);
                }
                return Observable.fromArray(fileSuggestions);
            });
    }

    public getId(): string {
        return 'FilesFastSuggestionProvider';
    }
}
